import {FormControl, FormGroup} from '@angular/forms';

import {forkJoin} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import {of} from 'rxjs/internal/observable/of';

import {
    ServerResponse,
    ServerResponseData,
    HttpStatusCodesEnum,
    UserRelationTypesEnum,
    ClientUserStatusesEnum,
    UsersService,
    CompanyEmployeesService,
    IndividualSubordinatesService,
    CountryModel,
    UserPhoneModel,
    CompanyEmployeeModel,
    ClientUserReportModel,
    IndividualSubordinateModel,
    CompanyEmployeeStatusTriggerModel,
    IndividualSubordinateStatusTriggerModel
} from '@spa/api';

import {InArrayValidator, PhoneValidator} from '@spa/validators';

import {Helper, SelectControl} from '../../../shared/classes';
import {PhoneAutocompleteValueInterface} from '../../../shared/widgets';
import {BaseControl, PhoneAutocompleteControl} from '../../../shared/classes/controls';

import {ClientUsersFormErrorStatesEnum} from '../enums/error-states';

import {
    ClientUsersFormControls,
    ClientUsersNameMinLength,
    ClientUsersFormStatusSelector,
    ClientUsersFormLastNameSelector,
    ClientUsersFormFirstNameSelector,
    ClientUsersFormMiddleNameSelector,
    ClientUsersPhoneNumberSelector,
} from '../../../../assets/descriptors';

export class ClientUsersForm {
    private readonly countries: CountryModel[];
    private readonly usersService: UsersService;
    private readonly clientUser: ClientUserReportModel;
    private readonly companyEmployeesService: CompanyEmployeesService;
    private readonly individualSubordinatesService: IndividualSubordinatesService;

    public form: FormGroup;
    public controls: BaseControl<string | number | PhoneAutocompleteValueInterface>[];

    constructor(
        countries: CountryModel[],
        usersService: UsersService,
        companyEmployee: ClientUserReportModel,
        companyEmployeesService: CompanyEmployeesService,
        individualSubordinatesService: IndividualSubordinatesService
    ) {
        this.countries = countries;
        this.usersService = usersService;
        this.clientUser = companyEmployee;
        this.companyEmployeesService = companyEmployeesService;
        this.individualSubordinatesService = individualSubordinatesService;
    }

    private initControl(
        control: BaseControl<string | number | PhoneAutocompleteValueInterface>,
        statusTriggers: number[]
    ): BaseControl<string | number | PhoneAutocompleteValueInterface> {
        switch (control.name) {
            case ClientUsersFormFirstNameSelector:
                control.value = this.clientUser.firstName;
                break;
            case ClientUsersFormMiddleNameSelector:
                control.value = this.clientUser.middleName;
                break;
            case ClientUsersFormLastNameSelector:
                control.value = this.clientUser.lastName;
                break;
            case ClientUsersFormStatusSelector:
                const statusTriggerIds = [];

                (control as SelectControl).options = statusTriggers.map((trigger: number) => {
                    const status = Helper.enumsHelper().getClientUserStatusByTrigger(trigger);

                    statusTriggerIds.push(trigger);

                    return {
                        label: Helper.textsHelper().getClientUserStatusName(status),
                        value: trigger,
                    };
                });

                const option = {
                    label: Helper.textsHelper().getClientUserStatusName(this.clientUser.status),
                    value: Helper.enumsHelper().getClientUserStatusTriggerByStatus(this.clientUser.status),
                };

                control.value = option.value;

                (control as SelectControl).options.push(option);

                statusTriggerIds.push(option.value);

                control.extendValidators(InArrayValidator(statusTriggerIds));
                break;
            case ClientUsersPhoneNumberSelector:
                const phoneCodes = this.countries.map((country: CountryModel) => country.phoneCode);

                (control as PhoneAutocompleteControl).countries = this.countries;

                control.validators.push(PhoneValidator(phoneCodes));

                if (this.clientUser.phoneNumber) {
                    const filteredPhoneCodes = this.countries.filter((country: CountryModel) => {
                        const phone = this.clientUser.phoneNumber;
                        const regex = new RegExp('^\\' + country.phoneCode);

                        return phone.match(regex) && (phone.match(regex)[0].length === country.phoneCode.length);
                    });

                    if (filteredPhoneCodes && filteredPhoneCodes.length > 0) {
                        control.value = {
                            code: filteredPhoneCodes[0].phoneCode,
                            phone: this.clientUser.phoneNumber.replace(filteredPhoneCodes[0].phoneCode, '')
                        };
                    }
                }

                break;
        }

        return control;
    }

    private updateStatus(deletePermissions: string[], authUserPermissions: string[], status: number): ServerResponse<boolean> {
        if (
            (status === ClientUserStatusesEnum.Deactivated) &&
            !Helper.getUnionStrategyPermissionsCheck(deletePermissions, authUserPermissions)
        ) {
            const errorResponse = Helper.errorsHelper().createBadRequestResponse(
                ClientUsersFormErrorStatesEnum.UpdateStatusToDeactivateRestricted
            );

            return of(errorResponse);
        }

        switch (this.clientUser.userTypeRelation) {
            case UserRelationTypesEnum.CompanyEmployee:
                const companyEmployeeStatusTriggerModel = new CompanyEmployeeStatusTriggerModel(
                    this.clientUser.customerId,
                    this.form.value[ClientUsersFormStatusSelector]
                );

                return this.companyEmployeesService.updateStatus(companyEmployeeStatusTriggerModel).pipe(
                    map((response: ServerResponseData<null>) => this.mapUpdateStatusResponse(response, status))
                );
            case UserRelationTypesEnum.IndividualSubordinate:
                const individualSubordinateStatusTriggerModel = new IndividualSubordinateStatusTriggerModel(
                    this.clientUser.customerId,
                    this.form.value[ClientUsersFormStatusSelector]
                );

                return this.individualSubordinatesService.updateStatus(individualSubordinateStatusTriggerModel).pipe(
                    map((response: ServerResponseData<null>) => this.mapUpdateStatusResponse(response, status))
                );
            default:
                const errorResponse = Helper.errorsHelper().createBadRequestResponse(
                    ClientUsersFormErrorStatesEnum.UpdateStatusFailed
                );

                return of(errorResponse);
        }
    }

    private mapUpdateStatusResponse(response: ServerResponseData<null>, status): ServerResponseData<boolean> {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            return Helper.errorsHelper().createErrorResponse(
                response,
                ClientUsersFormErrorStatesEnum.UpdateStatusFailed
            );
        }

        this.clientUser.status = status;

        return Helper.createSuccessResponse<boolean>(true);
    }

    private checkForUpdate(): boolean {
        return (
            (this.clientUser.firstName !== this.form.value[ClientUsersFormFirstNameSelector]) ||
            (this.clientUser.middleName !== this.form.value[ClientUsersFormMiddleNameSelector]) ||
            (this.clientUser.lastName !== this.form.value[ClientUsersFormLastNameSelector])
        );
    }

    private updateDetails(): ServerResponse<boolean> {
        switch (this.clientUser.userTypeRelation) {
            case UserRelationTypesEnum.CompanyEmployee:
                return this.updateCompanyEmployeeDetails();
            case UserRelationTypesEnum.IndividualSubordinate:
                return this.updateIndividualSubordinateDetails();
            default:
                const errorResponse = Helper.errorsHelper().createBadRequestResponse(
                    ClientUsersFormErrorStatesEnum.UpdateDetailsFailed
                );

                return of(errorResponse);
        }
    }

    private updateCompanyEmployeeDetails(): ServerResponse<boolean> {
        return this.companyEmployeesService.getCompanyEmployeeById(this.clientUser.customerId, true).pipe(
            switchMap((getResponse: ServerResponseData<CompanyEmployeeModel>) => {
                    if ((getResponse.status !== HttpStatusCodesEnum.Ok) || getResponse.data === null) {
                        return Helper.errorsHelper().createErrorResponseObservable<CompanyEmployeeModel>(
                            getResponse,
                            ClientUsersFormErrorStatesEnum.UpdateDetailsFailed
                        );
                    }

                    const companyEmployee = getResponse.data as CompanyEmployeeModel;

                    companyEmployee.firstName = this.form.value[ClientUsersFormFirstNameSelector];
                    companyEmployee.middleName = this.form.value[ClientUsersFormMiddleNameSelector];
                    companyEmployee.lastName = this.form.value[ClientUsersFormLastNameSelector];

                    return this.companyEmployeesService.updateCompanyEmployee(companyEmployee).pipe(
                        map((updateResponse: ServerResponseData<null>) => this.mapUpdateDetailsResponse(updateResponse))
                    );
                }
            )
        );
    }

    private updateIndividualSubordinateDetails(): ServerResponse<boolean> {
        return this.individualSubordinatesService.getIndividualSubordinateById(this.clientUser.customerId, true).pipe(
            switchMap((getResponse: ServerResponseData<IndividualSubordinateModel>) => {
                    if ((getResponse.status !== HttpStatusCodesEnum.Ok) || getResponse.data === null) {
                        return Helper.errorsHelper().createErrorResponseObservable<IndividualSubordinateModel>(
                            getResponse,
                            ClientUsersFormErrorStatesEnum.UpdateDetailsFailed
                        );
                    }

                    const individualSubordinate = getResponse.data as IndividualSubordinateModel;

                    individualSubordinate.firstName = this.form.value[ClientUsersFormFirstNameSelector];
                    individualSubordinate.middleName = this.form.value[ClientUsersFormMiddleNameSelector];
                    individualSubordinate.lastName = this.form.value[ClientUsersFormLastNameSelector];

                    return this.individualSubordinatesService.updateIndividualSubordinate(individualSubordinate).pipe(
                        map((updateResponse: ServerResponseData<null>) => this.mapUpdateDetailsResponse(updateResponse))
                    );
                }
            )
        );
    }

    private mapUpdateDetailsResponse(response: ServerResponseData<null>): ServerResponseData<boolean> {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            return Helper.errorsHelper().createErrorResponse(
                response,
                ClientUsersFormErrorStatesEnum.UpdateDetailsFailed
            );
        }

        this.clientUser.firstName = this.form.value[ClientUsersFormFirstNameSelector];
        this.clientUser.middleName = this.form.value[ClientUsersFormMiddleNameSelector];
        this.clientUser.lastName = this.form.value[ClientUsersFormLastNameSelector];

        return Helper.createSuccessResponse<boolean>(true);
    }

    private updatePhoneNumber(phone: string, code: string): ServerResponse<boolean> {
        const model = new UserPhoneModel(this.clientUser.userId, code, phone);

        return this.usersService.updatePhone(model).pipe(
            map((response: ServerResponseData<null>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponse<null>(
                        response,
                        ClientUsersFormErrorStatesEnum.UpdatePhoneFailed
                    );
                }

                this.clientUser.phoneNumber = (code + phone);

                return Helper.createSuccessResponse<boolean>(true);
            })
        );
    }

    initForm(statusTriggers: number[]): void {
        this.form = new FormGroup({});

        this.controls = ClientUsersFormControls()
            .map((control: BaseControl<string | number | PhoneAutocompleteValueInterface>) => {
                if (this.clientUser) {
                    control = this.initControl(control, statusTriggers);
                }

                this.form.addControl(control.name, new FormControl(control.value, control.validators));

                return control;
            });
    }

    validateForm(messageFunction: (controlLabel?: string, controlLength?: number) => { [key: string]: string; }): boolean {
        if (this.form.invalid) {
            Object.keys(this.controls).forEach((key: string) => {
                const control = this.controls[key];
                const formControl = this.form.get(control.name);

                if (formControl && formControl.invalid) {
                    const errorKeys = Object.keys(formControl.errors);

                    const errors = errorKeys.map((errorKey: string) => {
                        return messageFunction(control.placeholder, ClientUsersNameMinLength)[errorKey];
                    });

                    control.setErrors(errors);
                }
            });

            return false;
        }

        return true;
    }

    createRequest(deletePermissions: string[], authUserPermissions: string[]): ServerResponse<boolean> {
        const requestsStack = [];

        const code = (this.form.value[ClientUsersPhoneNumberSelector] as PhoneAutocompleteValueInterface).code;
        const phone = (this.form.value[ClientUsersPhoneNumberSelector] as PhoneAutocompleteValueInterface).phone;
        const status = Helper.enumsHelper().getClientUserStatusByTrigger(this.form.value[ClientUsersFormStatusSelector]);

        if (this.clientUser.status !== status) {
            requestsStack.push(this.updateStatus(deletePermissions, authUserPermissions, status));
        }

        if (this.checkForUpdate()) {
            requestsStack.push(this.updateDetails());
        }

        if (this.clientUser.phoneNumber !== [code, phone].join('')) {
            requestsStack.push(this.updatePhoneNumber(phone, code));
        }

        if (requestsStack.length > 0) {
            return forkJoin(requestsStack).pipe(
                map((responses: ServerResponseData<boolean>[]) => {
                    if (Helper.errorsHelper().checkForErrorResponses<boolean>(responses)) {
                        return Helper.errorsHelper().createReducedErrorResponse<boolean>(responses);
                    }

                    return Helper.createSuccessResponse<boolean>(true);
                })
            );
        }

        return Helper.createSuccessResponseObservable<boolean>(true);
    }
}
